import React, { useCallback, useRef, useState } from 'react';
import '../../../css/common/events/EventAssignmentConfirmModal.css';
import {
  assignCurrentAdminToEvents,
  getEventUuid,
  resolveUnassignedEventUuids,
} from '../../../utils/eventAssignments';
import PersonAvatar, { getPersonDisplayName } from '../avatar/PersonAvatar';
import DsButton from '../DsButton';
import DsModal from '../DsModal';

const normalizeEventUuids = (eventUuids) =>
  [...new Set(
    (Array.isArray(eventUuids) ? eventUuids : [eventUuids])
      .map((value) => String(value || '').trim())
      .filter(Boolean)
  )];

function EventAssignmentConfirmModal({
  open,
  title = 'イベント担当者への追加',
  message = 'あなたはこのイベントの担当者ですか？',
  confirmLabel = '自分を担当者に追加',
  cancelLabel = '今回は追加しない',
  submitting = false,
  error = '',
  eventCount = 0,
  currentAdmin = null,
  onConfirm,
  onCancel,
}) {
  if (!open) return null;

  return (
    <DsModal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      dismissible={!submitting}
      showClose={false}
      footer={(
        <>
          <DsButton
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={submitting}
          >
            {cancelLabel}
          </DsButton>
          <DsButton
            type="button"
            onClick={onConfirm}
            disabled={submitting}
          >
            {submitting ? '登録中...' : confirmLabel}
          </DsButton>
        </>
      )}
    >
        <div className="event-assignment-confirm__lead">
          <PersonAvatar
            person={currentAdmin}
            role="admin"
            name={getPersonDisplayName(currentAdmin, '管理者')}
            fallback="管"
            size={44}
          />
          <div className="event-assignment-confirm__body">
            <p className="event-assignment-confirm__message">{message}</p>
            <p className="event-assignment-confirm__context">
              現在の担当者設定はそのままに、あなたを追加します。
            </p>
          </div>
        </div>
        {eventCount > 1 ? (
          <p className="event-assignment-confirm__note">
            選択中の未担当イベント {eventCount} 件が対象です。
          </p>
        ) : null}
        {error ? (
          <p className="event-assignment-confirm__error" role="alert">
            {error}
          </p>
        ) : null}
    </DsModal>
  );
}

export const useEventAssignmentConfirmModal = ({ currentAdmin = null } = {}) => {
  const resolverRef = useRef(null);
  const dismissedEventIdsRef = useRef(new Set());
  const [state, setState] = useState(null);

  const resolveAndClose = useCallback((value) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    if (typeof resolve === 'function') {
      resolve(value);
    }
  }, []);

  const confirmAssignment = useCallback(({
    eventUuids,
    title = 'イベント担当者への追加',
    message = 'あなたはこのイベントの担当者ですか？',
    confirmLabel,
    cancelLabel,
    onAssigned,
    onSkipped,
  } = {}) => {
    const ids = normalizeEventUuids(eventUuids).filter(
      (eventUuid) => !dismissedEventIdsRef.current.has(eventUuid)
    );
    if (!ids.length) {
      return Promise.resolve({ confirmed: false, assigned: false });
    }

    if (typeof resolverRef.current === 'function') {
      resolverRef.current({ confirmed: false, assigned: false });
    }

    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        ids,
        title,
        message,
        confirmLabel,
        cancelLabel,
        onAssigned,
        onSkipped,
        submitting: false,
        error: '',
      });
    });
  }, []);

  const confirmAssignmentForEvents = useCallback(async ({
    events,
    adminId,
    ...modalOptions
  } = {}) => {
    const eventList = (Array.isArray(events) ? events : [events]).filter((event) => {
      const eventUuid = getEventUuid(event);
      return eventUuid && !dismissedEventIdsRef.current.has(String(eventUuid));
    });
    const eventUuids = await resolveUnassignedEventUuids(eventList, adminId);
    return confirmAssignment({ ...modalOptions, eventUuids });
  }, [confirmAssignment]);

  const handleCancel = useCallback(() => {
    (state?.ids || []).forEach((eventUuid) => dismissedEventIdsRef.current.add(eventUuid));
    if (typeof state?.onSkipped === 'function') {
      state.onSkipped();
    }
    resolveAndClose({ confirmed: false, assigned: false });
  }, [resolveAndClose, state]);

  const handleConfirm = useCallback(async () => {
    if (!state || state.submitting) return;
    setState((prev) => (prev ? { ...prev, submitting: true, error: '' } : prev));
    try {
      const result = await assignCurrentAdminToEvents(state.ids);
      if (typeof state.onAssigned === 'function') {
        state.onAssigned(result);
      }
      resolveAndClose({ confirmed: true, assigned: true, result });
    } catch (error) {
      setState((prev) =>
        prev
          ? {
              ...prev,
              submitting: false,
              error: error?.message || '担当イベントの登録に失敗しました。',
            }
          : prev
      );
    }
  }, [resolveAndClose, state]);

  return {
    confirmAssignment,
    confirmAssignmentForEvents,
    assignmentConfirmModal: (
      <EventAssignmentConfirmModal
        open={!!state}
        title={state?.title}
        message={state?.message}
        confirmLabel={state?.confirmLabel}
        cancelLabel={state?.cancelLabel}
        submitting={!!state?.submitting}
        error={state?.error}
        eventCount={state?.ids?.length || 0}
        currentAdmin={currentAdmin}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    ),
  };
};

export default EventAssignmentConfirmModal;
